import React from 'react';
import { t } from '../../onboarding/strings';
import { iconKind, assetUrl, iconGlyph, ASSET } from '../../onboarding/icons';
import './PhonePreview.css';

// Palette (extracted from the iOS SetupPages scene)
const C = {
  dark: '#1D1A23',
  sub: '#615F65',
  tertiary: '#898A8B',
  green: '#1FC27B',
  orange: '#D4942D',
  pink: '#FF5998',
  red: '#EB4F4F',
};

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */
/* ------------------------------------------------------------------ */

function Title({ children, size = 26 }) {
  return (
    <div className="pp-title" style={{ fontSize: size }}>
      {children}
    </div>
  );
}

// Gradient underline on a selected card (iOS "selected_gradient" asset).
function SelectedGrad() {
  return <div className="pp-opt-grad" style={{ backgroundImage: `url(${ASSET('selected_gradient.png')})` }} />;
}

// Real iOS icon (color brand / tinted monochrome) with emoji fallback.
function Icon({ name, selected, size = 36 }) {
  const kind = iconKind(name);
  if (kind === 'color') {
    return <img className="pp-icon" src={assetUrl(name)} alt="" style={{ width: size, height: size }} />;
  }
  if (kind === 'mono') {
    return (
      <span
        className="pp-icon pp-icon-mask"
        style={{
          width: size,
          height: size,
          WebkitMaskImage: `url(${assetUrl(name)})`,
          maskImage: `url(${assetUrl(name)})`,
          backgroundColor: selected ? C.dark : C.tertiary,
        }}
      />
    );
  }
  const g = iconGlyph(name);
  return g ? <span className="pp-opt-icon">{g}</span> : null;
}

function OptionCard({ option, selected }) {
  const desc = option.description ? t(option.description) : '';
  return (
    <div className={`pp-opt ${selected ? 'is-selected' : ''}`}>
      <div className="pp-opt-row">
        <div className="pp-opt-text">
          <div className="pp-opt-title">{t(option.title)}</div>
          {selected && desc ? <div className="pp-opt-desc">{desc}</div> : null}
        </div>
        <Icon name={option.icon} selected={selected} size={36} />
      </div>
      {selected ? <SelectedGrad /> : null}
    </div>
  );
}

function ListBody({ page }) {
  const content = page.content || {};
  const options = content.options || [];
  const isGrid = page.field === 'referral_source';
  const isMulti = content.type === 'list_multi_select';

  if (isGrid) {
    return (
      <>
        <Title>{t(page.title)}</Title>
        <div className="pp-grid">
          {options.map((o, i) => (
            <div key={o.id ?? i} className={`pp-grid-cell ${i === 0 ? 'is-selected' : ''}`}>
              <Icon name={o.icon} selected={i === 0} size={26} />
              <div className="pp-grid-title">{t(o.title)}</div>
              {i === 0 ? <SelectedGrad /> : null}
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <Title>{t(page.title)}</Title>
      <div className="pp-list">
        {options.map((o, i) => (
          <OptionCard key={o.id ?? i} option={o} selected={isMulti ? i < 2 : i === 0} />
        ))}
      </div>
    </>
  );
}

function RulerPicker({ value, unit, units }) {
  const ticks = Array.from({ length: 21 });
  return (
    <div className="pp-ruler-wrap">
      {units ? (
        <div className="pp-unit-toggle">
          {units.map((u) => (
            <span key={u} className={`pp-unit ${u === unit ? 'on' : ''}`}>
              {u}
            </span>
          ))}
        </div>
      ) : null}
      <div className="pp-bigvalue">
        {value} <span>{unit}</span>
      </div>
      <div className="pp-ruler">
        {ticks.map((_, i) => (
          <span key={i} className={`pp-tick ${i % 5 === 0 ? 'major' : ''} ${i === 10 ? 'center' : ''}`} />
        ))}
        <div className="pp-ruler-needle" />
      </div>
    </div>
  );
}

function MetricBody({ page }) {
  const map = {
    height: { value: 170, unit: 'cm', units: ['cm', 'ft'] },
    weight: { value: 70, unit: 'kg', units: ['kg', 'lb'] },
    age: { value: 25, unit: '', units: null },
  };
  const m = map[page.content.type] || map.weight;
  return (
    <>
      <Title>{t(page.title)}</Title>
      <div className="pp-center-fill">
        <RulerPicker value={m.value} unit={m.unit} units={m.units} />
      </div>
    </>
  );
}

function InputBody({ page, placeholder }) {
  return (
    <>
      <Title>{t(page.title)}</Title>
      <div className="pp-input">{placeholder}</div>
    </>
  );
}

function MotivationBody({ page }) {
  return (
    <div className="pp-stack-center">
      <div className="pp-emoji-hero">🔥</div>
      <Title size={24}>{t(page.title)}</Title>
    </div>
  );
}

function WelcomeBody({ page }) {
  const sub = t('onboarding_welcome_personalized_subtitle');
  return (
    <div className="pp-stack-center">
      <Title size={30}>{t(page.title)}</Title>
      <div className="pp-anim-box">Animation</div>
      {sub && sub !== 'onboarding_welcome_personalized_subtitle' ? (
        <div className="pp-subtitle">{sub}</div>
      ) : null}
    </div>
  );
}

function PhotoDemoBody({ page }) {
  return (
    <>
      <Title>{t(page.title)}</Title>
      <div className="pp-video">
        <div className="pp-play">▶</div>
      </div>
    </>
  );
}

function ReviewBody({ page }) {
  const reviews = (page.content && page.content.reviews) || [];
  return (
    <>
      <div className="pp-badge">millions of users choice</div>
      <img className="pp-avatar-group" src={ASSET('reviewers_avatar_group.png')} alt="" />
      <Title size={22}>{t(page.title)}</Title>
      <div className="pp-reviews">
        {reviews.slice(0, 3).map((r, i) => (
          <div key={i} className="pp-review-card">
            <div className="pp-stars">
              {Array.from({ length: 5 }).map((_, s) => (
                <img key={s} src={ASSET('review_star_fill.png')} alt="" />
              ))}
            </div>
            <div className="pp-review-msg">{t(r.message)}</div>
            <div className="pp-review-name">— {t(r.name)}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function AppleHealthBody({ page }) {
  return (
    <>
      <Title>{t(page.title)}</Title>
      <div className="pp-ah-card">
        <div className="pp-ah-top">
          <span className="pp-ah-label">Apple Health</span>
          <img className="pp-ah-icon" src={ASSET('apple_health.png')} alt="" />
        </div>
        <div className="pp-ah-setup">Setup</div>
      </div>
    </>
  );
}

function NotificationBody({ page }) {
  return (
    <div className="pp-stack-center">
      <div className="pp-notif-card">
        <div className="pp-notif-emoji">👆</div>
        <div className="pp-notif-msg">💪 {t(page.title)}</div>
        <div className="pp-notif-btns">
          <div className="pp-btn-dark">Allow</div>
          <div className="pp-btn-gray">Don't Allow</div>
        </div>
      </div>
    </div>
  );
}

function SettingUpBody({ page }) {
  const phase = (page.content && page.content.phase) || 1;
  const items = [
    'Saving your profile',
    'Calculating your metabolism',
    'Building your meal plan',
    'Personalizing your goals',
    'Finalizing',
  ];
  const done = 2;
  return (
    <div className="pp-stack-center" style={{ gap: 18 }}>
      <div className="pp-percent">68%</div>
      <div className="pp-progress">
        <div className="pp-progress-fill" style={{ width: '68%' }} />
      </div>
      <div className="pp-setup-title">{t(page.title)}</div>
      <div className="pp-checklist">
        {items.map((it, i) => (
          <div key={i} className={`pp-check ${i < done ? 'done' : ''}`}>
            <span className="pp-check-dot">{i < done ? '✓' : ''}</span>
            <span className="pp-check-label">{it}</span>
          </div>
        ))}
      </div>
      <div className="pp-phase-tag">phase {phase}</div>
    </div>
  );
}

function PrepurchaseBody({ page }) {
  return (
    <div className="pp-stack-center">
      <div className="pp-ring">🔔</div>
      <Title size={24}>We'll remind you before your free trial ends</Title>
    </div>
  );
}

function WeeklyGoalBody({ page }) {
  const cards = [
    { t: '0.25 kg / week', d: 'Slow & steady', sel: false },
    { t: '0.5 kg / week', d: 'Recommended', sel: true },
    { t: '0.75 kg / week', d: 'Ambitious', sel: false },
  ];
  return (
    <>
      <Title>{t(page.title)}</Title>
      <div className="pp-list">
        {cards.map((c, i) => (
          <div key={i} className={`pp-opt ${c.sel ? 'is-selected' : ''}`}>
            <div className="pp-opt-row">
              <div className="pp-opt-text">
                <div className="pp-opt-title">{c.t}</div>
                {c.sel ? <div className="pp-opt-desc">{c.d}</div> : null}
              </div>
            </div>
            {c.sel ? <SelectedGrad /> : null}
          </div>
        ))}
      </div>
    </>
  );
}

function NewGoalBody({ page }) {
  return (
    <>
      <Title>{t(page.title)}</Title>
      <div className="pp-info-cards">
        <div className="pp-info-card">
          <div className="pp-info-val">1,800</div>
          <div className="pp-info-sub">Initial daily budget</div>
        </div>
        <div className="pp-info-card">
          <div className="pp-info-val">Aug 24</div>
          <div className="pp-info-sub">Projected end date</div>
        </div>
      </div>
      <div className="pp-center-fill">
        <RulerPicker value={65} unit="kg" units={['kg', 'lb']} />
      </div>
    </>
  );
}

function MiniQuizBody({ page }) {
  return (
    <>
      <Title size={24}>{t(page.title)}</Title>
      <div className="pp-quiz">
        <div className="pp-quiz-btn correct">
          <div className="pp-quiz-emoji">🥗</div>
          <div>Whole foods</div>
        </div>
        <div className="pp-quiz-btn">
          <div className="pp-quiz-emoji">🍔</div>
          <div>Fast food</div>
        </div>
      </div>
      <div className="pp-quiz-explain">Whole foods keep you full longer for fewer calories.</div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Charts                                                              */
/* ------------------------------------------------------------------ */

function Ring({ percent = 92, color = C.green, label }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const off = c * (1 - percent / 100);
  return (
    <svg className="pp-ring-svg" viewBox="0 0 64 64">
      <circle cx="32" cy="32" r={r} stroke={color} strokeOpacity="0.18" strokeWidth="6" fill="none" />
      <circle
        cx="32"
        cy="32"
        r={r}
        stroke={color}
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={off}
        transform="rotate(-90 32 32)"
      />
      {label ? (
        <text x="32" y="37" textAnchor="middle" fontSize="14" fontWeight="700" fill={C.dark}>
          {label}
        </text>
      ) : null}
    </svg>
  );
}

function AhaRealisticBody({ page }) {
  return (
    <>
      <div className="pp-greenpill">✓ {t(page.title)}</div>
      <div className="pp-chart">
        <svg viewBox="0 0 260 130" width="100%" height="130">
          <defs>
            <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor={C.green} stopOpacity="0.32" />
              <stop offset="1" stopColor={C.green} stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <path d="M10,30 C70,40 120,95 250,110 L250,125 L10,125 Z" fill="url(#g1)" />
          <path d="M10,30 C70,40 120,95 250,110" fill="none" stroke={C.green} strokeWidth="3.5" strokeLinecap="round" />
          <circle cx="10" cy="30" r="5" fill={C.green} stroke="#fff" strokeWidth="2" />
          <circle cx="250" cy="110" r="6" fill={C.green} stroke="#fff" strokeWidth="2" />
        </svg>
      </div>
      <div className="pp-milestone">
        <Ring percent={92} label="92%" />
        <div className="pp-milestone-text">You're on track to hit your goal</div>
      </div>
    </>
  );
}

function AhaCompareBody({ page }) {
  return (
    <>
      <Title size={26}>{t(page.title)}</Title>
      <div className="pp-bars">
        <div className="pp-bar-col">
          <div className="pp-bar gray" style={{ height: 70 }} />
          <div className="pp-bar-x">1×</div>
          <div className="pp-bar-cap">Alone</div>
        </div>
        <div className="pp-bar-col">
          <div className="pp-bar green" style={{ height: 150 }} />
          <div className="pp-bar-x big">2×</div>
          <div className="pp-bar-cap">With KKAL</div>
        </div>
      </div>
    </>
  );
}

function AhaEvenSoonerBody({ page }) {
  return (
    <>
      <div className="pp-greenpill">⚡ {t(page.title)}</div>
      <div className="pp-chart">
        <span className="pp-saves-badge">Saves 12 days</span>
        <svg viewBox="0 0 260 130" width="100%" height="130">
          <path d="M10,30 C90,55 150,95 250,115" fill="none" stroke={C.tertiary} strokeOpacity="0.55" strokeWidth="2" strokeDasharray="4 4" />
          <path d="M10,30 C70,50 110,90 200,112" fill="none" stroke={C.green} strokeWidth="3.5" strokeLinecap="round" />
          <circle cx="10" cy="30" r="5" fill={C.green} />
          <circle cx="200" cy="112" r="6" fill={C.green} stroke="#fff" strokeWidth="2" />
          <circle cx="250" cy="115" r="5" fill={C.tertiary} fillOpacity="0.7" />
        </svg>
        <div className="pp-legend">
          <span><i style={{ background: C.tertiary }} /> Old plan</span>
          <span><i style={{ background: C.green }} /> Your plan</span>
        </div>
      </div>
    </>
  );
}

function PlanRevealBody({ page }) {
  const macros = [
    { name: 'Protein', g: '120g', color: C.green, icon: '🥩' },
    { name: 'Carbs', g: '220g', color: C.orange, icon: '🍞' },
    { name: 'Fats', g: '65g', color: C.pink, icon: '🥑' },
  ];
  return (
    <>
      <Title size={22}>{t(page.title)}</Title>
      <div className="pp-target-card">
        <div className="pp-target-label">DAILY CALORIC TARGET</div>
        <div className="pp-target-val">2,400 kcal</div>
      </div>
      <div className="pp-macros">
        {macros.map((m) => (
          <div key={m.name} className="pp-macro">
            <Ring percent={70} color={m.color} />
            <div className="pp-macro-icon">{m.icon}</div>
            <div className="pp-macro-g">{m.g}</div>
            <div className="pp-macro-name">{m.name}</div>
          </div>
        ))}
      </div>
      <div className="pp-datepill">🎯 Goal by Aug 24</div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Body dispatch + footer                                              */
/* ------------------------------------------------------------------ */

function renderBody(page) {
  const type = page.content && page.content.type;
  switch (type) {
    case 'list_single_select':
    case 'list_multi_select':
      return <ListBody page={page} />;
    case 'name':
      return <InputBody page={page} placeholder="Your name" />;
    case 'input_text':
      return <InputBody page={page} placeholder="Enter promo code" />;
    case 'motivation':
      return <MotivationBody page={page} />;
    case 'height':
    case 'weight':
    case 'age':
      return <MetricBody page={page} />;
    case 'welcome_personalized':
      return <WelcomeBody page={page} />;
    case 'photo_demo':
      return <PhotoDemoBody page={page} />;
    case 'review':
      return <ReviewBody page={page} />;
    case 'apple_health':
      return <AppleHealthBody page={page} />;
    case 'notpermission':
      return <NotificationBody page={page} />;
    case 'setting_up':
      return <SettingUpBody page={page} />;
    case 'prepurchase':
      return <PrepurchaseBody page={page} />;
    case 'weekly_goal':
      return <WeeklyGoalBody page={page} />;
    case 'new_goal':
      return <NewGoalBody page={page} />;
    case 'mini_quiz':
      return <MiniQuizBody page={page} />;
    case 'aha_realistic':
      return <AhaRealisticBody page={page} />;
    case 'aha_compare_2x':
      return <AhaCompareBody page={page} />;
    case 'aha_even_sooner':
      return <AhaEvenSoonerBody page={page} />;
    case 'plan_reveal':
      return <PlanRevealBody page={page} />;
    default:
      return (
        <div className="pp-stack-center">
          <Title size={22}>{t(page.title)}</Title>
          <div className="pp-subtitle">Unsupported type: {String(type)}</div>
        </div>
      );
  }
}

// Footer button per type: null = no footer button.
function footerFor(type) {
  if (type === 'setting_up' || type === 'notpermission') return null;
  if (type === 'prepurchase') return { label: 'Try for $0.00', kind: 'dark' };
  return { label: 'Continue', kind: 'dark' };
}

export default function PhonePreview({ page, index = 0, total = 1 }) {
  if (!page) {
    return (
      <div className="pp-phone">
        <div className="pp-screen pp-empty">Select a page to preview</div>
      </div>
    );
  }

  const type = page.content && page.content.type;
  const footer = footerFor(type);
  const denom = Math.max(total, 1) + 1;
  const pct = Math.min(100, Math.round(((index + 1) / denom) * 100));

  return (
    <div className="pp-phone">
      <div className="pp-screen">
        <img className="pp-bg" src={ASSET('bg_blures.png')} alt="" />
        <div className="pp-header">
          <div className="pp-header-top">
            {index > 0 ? (
              <img className="pp-back" src={ASSET('left_arrow.png')} alt="back" />
            ) : (
              <span className="pp-back-spacer" />
            )}
            <img className="pp-logo" src={ASSET('logo_text.png')} alt="KKAL" />
            <span className="pp-back-spacer" />
          </div>
          <div className="pp-progress-track">
            <div className="pp-progress-bar" style={{ width: pct + '%' }} />
          </div>
        </div>

        <div className="pp-body">{renderBody(page)}</div>

        {footer ? (
          <div className="pp-footer">
            <div className={`pp-cta ${footer.kind}`}>{footer.label}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
